import React from 'react';
import { AnyFunc } from '@/utils/types';

/**
 * 渲染的元素类型：
 * - 'button' 使用 antd-mobile Button
 * - 'a' 使用原生 <a>
 * - 'withPopover' 使用 Popover 包裹的 <a>
 */
export enum ActionElementType {
  button = 'button',
  a = 'a',
  withPopover = 'withPopover'
}

/**
 * 自定义Button组件的props接口定义
 */
export interface ActionButtonProps {
  /** 选择渲染的按钮类型（默认 'button'） */
  as?: ActionElementType;
  /** 子节点内容（文本 / 自定义 ReactNode） */
  children?: React.ReactNode;
  /** 是否禁用 */
  disabled?: boolean;
  /** 自定义类名 */
  className?: string;
  /** 自定义内联样式 */
  style?: React.CSSProperties;
  /** 点击逻辑（内部会传入 handleClose 作为第一个参数） */
  onOk?: AnyFunc;
  /** 关闭回调（成功 / 非 Promise 立即执行时调用） */
  clickFinishedCb?: (...args: any[]) => void;
  /** antd-mobile Button 颜色语义 */
  amType?: 'primary' | 'default' | 'warning' | 'success' | 'danger';
  /** antd-mobile Button 填充风格 */
  amFill?: 'solid' | 'outline' | 'none';
  /** antd-mobile Button 尺寸 */
  amSize?: 'mini' | 'small' | 'middle' | 'large';
  /** antd-mobile Button 是否块级（等价于 block） */
  amBlock?: boolean;
  /** a 标签跳转地址（as='a' 或 withPopover 内部 anchor 时可用） */
  href?: string;
  /** a 标签 target */
  target?: string;
  /** 是否阻止事件冒泡（默认 true） */
  stopPropagation?: boolean;
  /** 是否阻止默认行为（默认 true） */
  preventDefault?: boolean;
  /** 是否在点击期间阻止页面 touch 滚动（默认 true） */
  preventTouchScroll?: boolean;
  /** 是否自动聚焦（默认 false） */
  autoFocus?: boolean;
  /** 处于禁用态时是否仍允许触发点击逻辑（默认 false） */
  clickableWhenDisabled?: boolean;
  /** touch-action，用于减少误触滚动（默认 'manipulation'） */
  touchAction?: 'auto' | 'none' | 'manipulation';
  /** overscroll-behavior，用于抑制边界回弹（默认 'contain'） */
  overscrollBehavior?: 'auto' | 'contain' | 'none';
  /** as='withPopover' 时的 Popover 配置 */
  popover?: {
    /** 自定义内容（与 actions 互斥，优先级更高） */
    content?: React.ReactNode;
    /** 快捷动作列表 */
    actions?: Array<{
      /** 唯一 key */
      key: string;
      /** 展示文本 */
      text: string;
      /** 可选图标 */
      icon?: React.ReactNode;
      /** 是否禁用 */
      disabled?: boolean;
      /** 点击回调 */
      onClick?: () => void;
    }>;
    /** 弹出位置 */
    placement?: any;
    /** 触发方式（click / hover） */
    trigger?: 'click' | 'hover';
    /** 受控可见性 */
    visible?: boolean;
    /** 可见性变化回调 */
    onVisibleChange?: (visible: boolean) => void;
    /** 自定义渲染容器 */
    getContainer?: () => HTMLElement;
    /** 隐藏后是否销毁内容 */
    destroyOnHide?: boolean;
    /** 是否阻止事件冒泡（默认 true） */
    stopPropagation?: boolean;
    /** Popover 自定义类名 */
    className?: string;
    /** Popover 自定义样式 */
    style?: React.CSSProperties;
  };
  /** 测试标识符，用于自动化测试 */
  'data-testid'?: string;
}

/**
 * 渲染按钮时提供的上下文
 */
export interface ActionButtonRenderContext {
  /** 传递给底层元素的 className */
  className: string;
  /** 合并后的 style（包含手势相关属性） */
  style: React.CSSProperties;
  /** 子节点内容 */
  children: React.ReactNode;
  /** 是否 loading */
  loading: boolean;
  /** 是否禁用 */
  disabled: boolean;
  /** 包裹后的点击处理器 */
  handleClick: (e: React.MouseEvent<any>) => void;
  /** 底层元素 ref */
  btnRef: React.MutableRefObject<HTMLButtonElement | HTMLAnchorElement | null>;
  /** a 标签 href */
  href?: string;
  /** a 标签 target */
  target?: string;
  /** antd-mobile Button 颜色语义 */
  amType: string;
  /** antd-mobile Button 填充风格 */
  amFill: string;
  /** antd-mobile Button 尺寸 */
  amSize: string;
  /** antd-mobile Button block */
  amBlock?: boolean;
  /** Popover 配置 */
  popover?: ActionButtonProps['popover'];
  /** 测试标识符 */
  'data-testid'?: string;
}
